import { assign, escape, unescape } from 'lodash';
import { createLogger } from 'shared/services/logger';
import us from '../../../defines';
import { BoundingBox, Point } from '../../../lib/objects/index';
import {
  assocArrayLength,
  hexColortoRGB,
  isDefined,
  isUnDefined,
  reverseCLR,
  roundToDecimals,
  roundToNearset
} from '../../../lib/utility/helpers';

const logger = createLogger('UScope:extensions:annotations:Region');

// ________________________________________________________________________________________________
//    Object Name: Region
//    Overview:  The Region object is a container for region information and is used as a reference
//                between graphics in raphael paper and the annotation xml.
//
// ________________________________________________________________________________________________

//    Region
//    inObj:        req'd, object containing properties for to-be-created region object
//    inObjType:    req'd, type of inObj: {'json', 'x2js' or 'jquery'}
//    vpObj:        req'd, cViewport object
//    aLayer:        req'd only when inObjType == 'x2js'; a ref the cLayer this region belongs to
//    mu : MarkUp
export default function Region (props, propSource, viewport, layer, annotationsService, isExported) {
  var self = this;
  var paper = annotationsService.paper;

  var CREGION = this;
  var CREGION_GET = this;
  var CREGION_SET = this;
  var CREGION_MARKUP = this;
  var CREGION_ANALYSIS = this;
  var CREGION_POINTS = this;

  self.regionId = 0; // current or new region id - never equal to 0
  self.displayId = 0; // display id
  // note that selected is not maintained in this object
  self.Analyze = false; // means 'can run analysis', s/b in agreement with us.eAnAnalysis[self.type]

  self.type = -1; // as given by us.eAnTypes[]
  self.shape = undefined; // as given by us.anShapes[]
  self.decRGB = undefined; // decimal value of color in RGB order
  self.decBGR = undefined; // and color in BGR order too

  self.zoom = 1.0; // zoom at time of orignal drawing
  self.pts = []; // array of Points at base zoom

  self.note = undefined; // text associated with this region
  self.data = {}; // associative array for related data such as caliper distance measures

  // analysis related
  self.InputRegionId = undefined; // parent region, 0 means no parent (not analysis),  -1 means whole image
  self.MarkupImageId = undefined; // either undefined or valid markup image id with the @ sign for internal use (saved without it)
  self.MacroId = undefined; // copied from layer
  self.MacroName = undefined; // copied from layer

  // derived .............................................
  self.isHighLight = false; // indicate region for hightlight outline
  self.isReportRegion = false; // indicate report region status
  self.hasEventHandlerHighlight = false; // has an eventHandle assigned

  self.openShape = false; // openShape is a flag for rfDraw, when true the form is not closed(while user is drawing); matters only to pen, polygon,

  self.imageId = viewport ? viewport.getImageId() : 0;
  self.layerId = 0; // associated layer id; never is a layer id 0

  // The "rfHandle" is a reference to the raphael wrapper around the SVG DOM node.

  self.rfId = -1; // raphael id and used as index of region in rfRegions[]

  // geometry
  self.centerPt = new Point(-1, -1); // computed center of pts array, use getCenter() to compute & access
  self.boundingBox = new BoundingBox(0, 0, 0, 0); // region or markup bounding box in base coords (pixels) at zoom = 1
  self.angleDeg = 0.0; // view rotation angle at time of orignal drawing

  // analysis and markup image related
  self.isSmall = false; // relatively speaking, is this a small region at scan resolution? see notes on us.IMAGESERVER_MAXJDIM
  self.isAnalyzed = false;
  self.hasMarkup = false; // has an associated analysis markup image
  self.isWholeSlide = false; // whole image analysis region
  self.isWholeSlideMarkup = false; // whole image analysis region with markup

  // markup xinfo
  self.xinfo = undefined; // markup image xinfo
  self.muInitialized = false; // markup image xinfo initialized
  self.AlgorithmName = undefined; // markup algorithm name
  self.tileWidth = 512;
  self.tileHeight = 512; // markupmarkup tile dimensions; default to something so even if xinfo for markup isn't yet returned the render will work

  // flag indicating whether this region is being copied or moved. Set by region-interaction.
  self.isCopyMoving = false;

  /// ///////////////////////////////////////////////////todo: ia related .... still in work
  var attributes = {};
  CREGION_ANALYSIS.getRegionResults = function () {
    return attributes;
  };
  CREGION_ANALYSIS.getRegionResultsLength = function () {
    return assocArrayLength(attributes);
  };
  CREGION_ANALYSIS.clearRegionResults = function () {
    attributes = {};
  };
  /// ///////////////////////////////////////////////////

  CREGION_GET.get = function (str) {
    return this[str];
  };
  CREGION_SET.set = function (key, value) {
    this[key] = value;
  };

  // ............................................ simple gets
  CREGION_GET.getType = function () {
    return self.type;
  };
  CREGION_GET.getShape = function () {
    return self.shape;
  }; // todo: better is use anShapes[] and don't keep this var
  CREGION_GET.getZoom = function () {
    return self.zoom;
  };
  CREGION_GET.getRegionId = function () {
    return self.regionId;
  };
  ///    region_get.getRfId = function () { return self.rfId; };
  CREGION_GET.getRGB = function () {
    return self.decRGB;
  };
  CREGION_GET.getBGR = function () {
    return self.decBGR;
  };

  CREGION_GET.getLayerId = function () {
    return +self.layerId;
  };

  CREGION_ANALYSIS.getInputRegionId = function () {
    return self.InputRegionId;
  }; //
  CREGION_ANALYSIS.getMacroId = function () {
    return self.MacroId;
  }; // from layer originally
  CREGION_ANALYSIS.getMacroName = function () {
    return self.MacroName;
  }; // from layer originally

  // ............................................ Region markupIsInView
  //    markupIsInView: returns true if region has a markup and it is in the passed view rect; otherwise false;
  CREGION_MARKUP.markupIsInView = function (cr2View) {
    return ((self.hasMarkup) ? self.isInView(cr2View) : false);
  };
  // ............................................ Region getMarkupInfo
  var markupInfo = {}; // Region local var, cached markup info
  CREGION_MARKUP.getMarkupInfo = function () {
    if (markupInfo.AlgorithmName == undefined) { // if this xinfo var has not been set in the object then the object has not been populated yet
      computeGeometry_(); // insure geometry is up to date
      markupInfo = {
        hasMarkup: self.hasMarkup,
        muInitialized: self.muInitialized,
        AlgorithmName: self.AlgorithmName,
        // could get macro name and id from layer? getMacroName()
        regionId: self.regionId,
        MarkupImageId: self.MarkupImageId,
        boundingBox: self.boundingBox,
        zoom: self.zoom,
        isSmall: self.isSmall,
        tileWidth: self.tileWidth,
        tileHeight: self.tileHeight
      };
    }
    return markupInfo;
  };
  // ............................................ Region constructor
  // This function construct the region given either a json or x2js input object.
  // notes:
  //        o event handlers not added because rfId is needed
  //        o analysis results including markup imagery are not fetched at this time

  this.updateProps = function (props, propSource, layer) {
    if (propSource === 'json') {
      var json = props;
      assign(self, props);

      if (self.rfHandle) {
        self.rfId = self.rfHandle.id;
      }
      if (isDefined(json.type)) {
        // remember this sets things related to type as well
        self.setType(+json.type);
      }
      if (json.decRGB) {
        self.setRGB(json.decRGB);
      }
      if (json.decBGR) {
        self.setBGR(json.decBGR);
      }
      if (json.MarkupImageId) {
        self.MarkupImageId = json.MarkupImageId;
      }

      if (self.shape == 'caliper' || self.shape == 'polygon') { // update length for shapes that require it
        self.computeLength();
      }
    } else if (propSource == 'x2js') { // input is x2js object? x2js is used to create from xml input from dataserver
      if (!layer) { // layer is required input
        logger.error('Layer ref is required for object type x2js');
        return;
      }
      if (!(props.Vertices && props.Vertices.Vertex_asArray)) { // sometimes regions without vertices are showing,  so just abort region creation
        logger.error('Object type x2js does not contain vertices');
        return;
      }

      // should I get the layer name, it can change right?
      self.MacroId = layer.get('MacroId'); // cache the layer info needed for analysis presentation
      self.MacroName = layer.get('MacroName');

      self.setRegionId(props._Id); // '_item' is access to attribute 'item' of Region node
      // todo: probably if a region is selected then its layer should be selected
      // ?move back is currently in annot?            var Selected = props._Selected === "1" ? true : false;///if moved here, update onSelectChange()

      if (props._MarkupImageId) { // this an analysis output region if it has markup. TODO: Treat all region data the same regardless if IA or not
        self.InputRegionId = (props._InputRegionId && props._InputRegionId != '0') ? +props._InputRegionId : undefined;
        //  a value of -1 means whole image or tuning region
        self.isAnalyzed = true;
        /// /////self.isWholeSlide = (self.InputRegionId < 0) ? true : false;
        if (self.InputRegionId < 0) { // InputRegionId is negative for tuning region and for whole slide
          if (self.regionId < 0) self.isTuningRegion = true; // regionId is negative for tuning region and positive for whole slide
          /// todo: remove layer's knowledge?  self.isTuningRegion = layer.get('isTuningLayer');
          self.isWholeSlide = !!self.isTuningRegion;
        }

        // note: that the markup image is dimensioned only as large as it needs to be;  it is tiled but these tiles are not registered with image tiles, they align with the ROI

        //  Use ImageLocation to get MarkupImageId because it is defined for tuning, wholeslide and analysis output regions
        //            TuningRegion  WholeSlide    AnalysisInput  AnalysisOutput
        //            ------------  ----------    -------------  --------------
        //  MarkupImageId    undefined    defined      undefined    defined
        //  ImageLocation    defined      defined      undefined    defined
        self.MarkupImageId = props._MarkupImageId; // the imageId of a markup, can be undefined
        self.TempImageLocation = props._TempImageLocation ? props._TempImageLocation : undefined; // tuning only: the server's path to the markup image

        self.hasMarkup = !!self.MarkupImageId;
        self.isWholeSlideMarkup = self.isWholeSlide ? self.hasMarkup : false; // flag that this region is whole image analysis with a markup
        ///        self.isWholeSlideMarkup = (self.InputRegionId < 0) ? self.hasMarkup : false; //flag that this region is whole image analysis with a markup

        // todo: move this search to RegionList
        var regionParent = (self.InputRegionId != -1) ? annotationsService.getRegionsObj().getByRegionId(self.InputRegionId) : undefined; // the parent region of this region
        if (regionParent) {
          var anType = regionParent.get('type'); // if Type is from parent region b/c iscope keeps changing output region rects to pen
          self.setType(anType);
          self.zoom = regionParent.get('zoom');
        } else { // valid InputRegionId but no regionParent
          // Try to derive the region's shape. It'll fail to render if we're wrong.
          if (props.Vertices.Vertex_asArray.length === 5) { // The first and last points are the same in a rectangle
            self.setType(us.eAnTypes.rect);
          } else {
            self.setType(us.eAnTypes.pen);
          }
          self.zoom = 1.0;
        }

        if (props.Attributes) {
          var IdNameLUT = layer.getRegionResultsLUT();

          var regAttrs = props.Attributes;
          for (var a = 0, alen = regAttrs.__cnt; a < alen; a++) {
            var attr = regAttrs.Attribute[a];
            if (attr == undefined) { // because of same problem mentioned for layer attributes
              break;
            }

            var Name = IdNameLUT[attr._Name] ? IdNameLUT[attr._Name] : attr._Name;
            attributes[Name] = attr._Value;
            if (IdNameLUT[attr._Name] == undefined) { // if the look up failed
              logger.error('Region Attributes numeric name: ' + attr._Name + ' has no match in Regions.RegionAttributeHeaders');
            }
          }
        }
        // no DisplayId??? but defined on output regions
      } else { // this is a non-analysis region
        self.setType(props._Type);
        var NegativeROA = props._NegativeROA == '1';
        if (self.type == us.eAnTypes.pen && NegativeROA) { // if negativeROA indicated, switch to negative pen type
          self.setType(us.eAnTypes.negpen);
        }

        var Zoom = 1.0 / props._Zoom; // the annotation zoom is the inverse of the image zoom
        self.zoom = roundToDecimals(Zoom);

        // not impl var ImageFocus = props._ImageFocus;

        self.isReportRegion = layer.get('isReportLayer');
      }

      self.displayId = props._DisplayId;
      self.Analyze = !!us.eAnAnalysis[self.type]; // means can run analysis

      // LineColor for a region is only valid on the layer named 'Annotation'
      var RegionBGR = (layer.get('isAnnotLayer') && props._LineColor) ? props._LineColor : layer.get('LineColor');
      self.setBGR(RegionBGR);

      var jsVertex = props.Vertices.Vertex_asArray;
      self.pts = jsVertexToPtList(jsVertex);

      if (props._Length) { // ImageScope saves attribute "Length" (pixels)
        self.data.lengthPixels = props._Length; // add additional info to region.data
        self.data.lengthMicrons = distance_({'value': self.data.lengthPixels, 'toMicrons': true});
      } else if (props._LengthMicrons) { // WebScope saves attribute "LengthMicrons"
        self.data.lengthMicrons = props._LengthMicrons;
        self.data.lengthPixels = (self.data.lengthMicrons / viewport.getBaseMPP()).toFixed(1);
      }
      // todo: what to do about area input?
      // if (jRegion.attr("Area")) {                                // ImageScope saves attribute "Area" (pixels)
      // else if (jRegion.attr("AreaMicrons")) {                    // WebScope saves attribute "AreaMicrons"

      self.note = props._Text ? unescape(props._Text) : '';
    } else {
      logger.error('construct_: unrecognized propSource: ' + propSource);
      return;
    }

    // Create layer association
    if (self.layerId) {
      self.layer = annotationsService.layers.getLayerById(self.layerId);
    } else {
      self.layer = layer || annotationsService.layers.getSelected();
      if (layer) { // "hilite" regions don't belong to any layer
        self.layerId = self.layer.get('Id');
      }
    }

    if (self.decRGB === undefined) { // if region color was not defined, randomly select a color
      self.setRGB(hexColortoRGB(us.getRandomColor()), false);
    }
    computeGeometry_(); //

    return true;

    //    jsVertexToPtList - This function converts a x2js object of x,y vertices to an array of Points
    function jsVertexToPtList (jsVertex) {
      var ptList = [];
      for (var v = 0, vlen = jsVertex.length; v < vlen; v++) {
        var x = jsVertex[v]._X;
        var y = jsVertex[v]._Y;
        var pt = new Point(x, y);
        ptList.push(pt);
      }
      return ptList;
    }
  };

  function draw () {
    self.rfHandle = annotationsService.rfDraw(paper, self, viewport.getViewZoom(), viewport.getViewAngle(true)); // draw this graphics object
    if (self.rfHandle) {
      self.rfId = self.rfHandle.id;
    } else {
      logger.warn('Region::construct_: rfId: ' + self.rfId + ' Drawing failed');
    }
  }

  CREGION.remove = function () {
    CREGION.removeGraphics();
    if (layer) {
      layer.removeRegion(self);
    }
  };

  CREGION.removeGraphics = function () {
    if (self.rfHandle) { // remove the region from the graphics paper
      self.rfHandle.remove();
    }

    if (self.data.rfTextHandle) { // remove any custom handles
      self.data.rfTextHandle.remove();
    }
    if (self.data.textBBoxHandle) {
      self.data.textBBoxHandle.remove();
    }
  };

  // ............................................ addEventHandlers()
  // note: event handlers for region can only be added after region is drawn and has a valid rfHandle
  CREGION.addEventHandlers = function (json) {
    if (!self.isWholeSlide && self.rfHandle) {
      if (json.click) {
        self.rfHandle.click(cb_regClick);
      }
      if (json.hover) {
        self.rfHandle.node.addEventListener('mouseover', cb_regHover, false); // Use eventListener instead of the event handlers
      }
      if (json.textclick) {
        const rfTextHandle = self.data.rfTextHandle;
        if (rfTextHandle) {
          rfTextHandle.click(cb_regClick); // register the buddy text for on click events
          rfTextHandle.data('parentRfId', self.rfId); // save parent id so it can be used as the highlighted graphic
        }
      }
    } else {
      logger.error(' addEventHandlers: not added, rfHandle: ' + self.rfHandle);
    }

    // add the contextmenu to the region using eventListener
    self.rfHandle.node.addEventListener('contextmenu', (event) => {
      viewport.events.trigger('us:annotations:show-context-menu', {event, region: self});
    });

    // This function sorts out the parent region for a click (associated text is a child region)
    function cb_regClick () {
      // note that "self" is Region, "this" is raphael object
      if (isDefined(this.id)) {
        const parentId = this.data('parentRfId');
        if (isDefined(parentId)) { // if the annot has a parent, select the parent  (buddy text annots have parentRfId)
          annotationsService.regSelect({rfId: parentId, bReposition: false}); // unselect the currently selected parent object and select this one
        } else {
          annotationsService.regSelect({rfId: this.id, bReposition: false}); // unselect the currently selected object and select this one
        }
      }
    }

    // This function is the region hover callback. It displays a hovering note associated with the region, if it exists.
    function cb_regHover () {
      annotationsService.regHoverShow(true, self.rfId); // note that "self" is Region, "this" is raphael object
    }
  };

  CREGION_GET.isSelected = () => {
    return annotationsService.rfSelected === self.rfId;
  };

  // ............................................ simple computations
  CREGION.computeLength = function () {
    if (self.getPtsLen()) {
      self.data.lengthPixels = distance_({'pointList': self.getPts()});
      self.data.lengthMicrons = distance_({'value': self.data.lengthPixels, 'toMicrons': true});
    }
  };
  // ............................................ setRegionId
  CREGION_SET.setRegionId = function (regionId) {
    self.regionId = Number(regionId);
  };
  // ............................................ setType
  CREGION_SET.setType = function (type) {
    self.type = +type;
    self.shape = us.anShapes[self.type];
    self.openShape = !!((self.type == us.eAnTypes.pen || self.type == us.eAnTypes.caliper));
    self.Analyze = !!us.eAnAnalysis[self.type]; // means can run analysis
  };
  // ............................................ setRGB
  CREGION_SET.setRGB = function (decRGB) {
    self.decRGB = decRGB;
    self.decBGR = reverseCLR(decRGB);
  };
  // ............................................ setBGR
  CREGION_SET.setBGR = function (decBGR) {
    self.decBGR = decBGR;
    self.decRGB = reverseCLR(decBGR);
  };
  // ............................................ region onSelectChange
  //        bSelect:        required, true/false, save state as selected/deselected
  //        cb_function:    optional, response callback function
  CREGION_SET.onSelectChange = function (bSelect) {
    // note that the region doesn't keep a var that it is selected

    bSelect = (isUnDefined(bSelect) || bSelect == false) ? 0 : 1; // convert bSelect to 0,1
    var layerXML = '<Annotation Id="' + self.layerId + '" Selected="' + bSelect + '">'; // do not include closing tag
    var regionXML = '<Region Id="' + self.regionId + '" Selected="' + bSelect + '"></Region>';

    annotationsService.save({
      action: 'updateRegionAndLayer',
      layerId: self.layerId,
      layerXML: layerXML,
      regionId: self.regionId,
      regionXML: regionXML
    });
  };
  // ............................................ region addNote
  CREGION.addNote = function (note = '') {
    self.note = noteFormat_(note); // limit # chars and insert line feeds for display

    var regionXML = '<Region Id="' + self.regionId + '"' + ' Text="' + escape(self.note) + '"' + '></Region>';
    annotationsService.save({
      action: 'updateRegion',
      layerId: self.layerId,
      regionXML: regionXML
    });

    // ..................................................
    // closure functions
    // ............................................ noteFormat
    //    noteFormat - This function limits the number of characters and adds newlines for display.
    function noteFormat_ (note) {
      if (note[note.length - 1] === '\n') { // strip off trailing carriage return
        note = note.substr(0, note.length - 1);
      }
      var oNote = note.substring(0, us.textEditSpecs.maxChars); // limit number of characters
      var words = oNote.split(' '); // split into words
      var rtnNote = ''; // return string

      var line = words[0]; // line of text
      var goalWidth = us.textEditSpecs.noteWidth; // display note width
      for (var i = 1, len = words.length; i < len; i++) {
        var crIndex = words[i].indexOf('\n');

        if (crIndex != -1) {
          var words2 = words[i].split('\n'); // check for, handle user-defined carriage returns
          if (words2.length == 2) { // two words with user-defined cr between them
            rtnNote += line + ' ' + words2[0] + '\n';
            line = words2[1];
            continue;
          } else if (crIndex == 0) { // user-defined cr at start of word
            line += '\n';
            words[i] = words2[0];
            line = words2[1];
            continue;
          } else { // user-defined cr at end of word
            rtnNote += line + ' ' + words2[0] + '\n';
            line = '';
            continue;
          }
        }
        if (words[i].length + line.length < goalWidth) {
          line += ' ' + words[i];
        } else {
          rtnNote += line + '\n';
          line = words[i];
        }
      }
      if (line.length) rtnNote += line;
      return rtnNote;
    }

    // ..................................................
  };

  // ............................................ related to points
  CREGION_POINTS.clrPts = function () {
    self.pts = [];
  };
  CREGION_POINTS.getPts = function () {
    return self.pts;
  };
  CREGION_POINTS.getPtsLen = function () {
    return (self.pts) ? self.pts.length : 0;
  };
  CREGION_POINTS.setPtsLen = function (len) {
    if (len > -1) self.pts.length = len;
  };
  CREGION_POINTS.getPt = function (index) {
    return (index < self.pts.length) ? self.pts[index] : undefined;
  };
  CREGION_POINTS.addPt = function (pt) {
    self.pts.push(pt);
  };
  CREGION_POINTS.insertPt = function (pt, at) {
    self.pts.splice(at, 0, pt);
  };
  CREGION_POINTS.setPt = function (pt, index) {
    self.pts[index] = pt;
  };

  // ............................................ region rotatePtsAbout
  CREGION_POINTS.rotatePtsAbout = function (angleDeg, cpOrigin, opt_precision) {
    if (angleDeg == 0) return;

    for (var p = 0, len = self.pts.length; p < len; p++) {
      self.pts[p].rotateAbout(angleDeg, cpOrigin, opt_precision);
    }

    return self.pts.length;
  };
  // ............................................ region copyPts
  CREGION_POINTS.copyPts = function () {
    var curPts = self.pts;
    var newPts = [];
    for (var p = 0, len = curPts.length; p < len; p++) {
      var pt = new Point(curPts[p].x, curPts[p].y);
      newPts.push(pt);
    }
    return newPts;
  };
  // ............................................ replacePts
  CREGION_POINTS.replacePts = function (ptList) {
    self.clrPts();
    if (ptList && ptList.length) {
      for (var p = 0, len = ptList.length; p < len; p++) {
        self.pts.push(new Point(ptList[p].x, ptList[p].y));
      }
    }
    return self.pts;
  };
  // ............................................ centerAt
  CREGION_POINTS.centerAt = function (cpCenterNew) {
    var cpCenterCur = self.getCenter();
    var dx = cpCenterNew.x - cpCenterCur.x;
    var dy = cpCenterNew.y - cpCenterCur.y;

    for (var p = 0, len = self.pts.length; p < len; p++) {
      self.pts[p].x += dx;
      self.pts[p].y += dy;
    }
    self.getCenter(us.WITHFORCE); // force update of center point
  };
  // ............................................ thinOutPts
  CREGION_POINTS.thinOutPts = function (minDist) {
    var nPtsRemoved = 0;
    if (self.type != us.eAnTypes.pen) return nPtsRemoved; // only thin out pen drawings

    var newPts = self.copyPts();
    var len = newPts.length;
    if (len > 5) {
      var TOOCLOSE = (minDist == undefined) ? 5 : minDist; // define what too closely spaced means
      var pt0 = newPts[0]; // left-adjacent point
      for (var p = 1; p < len; p++) { // compare adjacent point city block distance
        var pt1 = newPts[p]; // right-adjacent point
        if (pt1.cityBlockDistance(pt0) < TOOCLOSE) { // if left and right adjacent points are"too close"
          newPts[p].set(-1, -1); // then flag that the right one to be removed
          nPtsRemoved++;
        } else {
          pt0 = pt1; // otherwise the left-adjacent becomes the right adjacent
        }
      }
      var lastPt = newPts[len - 1]; // test the last point against the first point
      if (lastPt.x != -1 && newPts[0].cityBlockDistance(lastPt) < TOOCLOSE) {
        newPts[len - 1].set(-1, -1); // and flag that the last point to be removed
      }

      self.clrPts(); // copy valid points from newPts to self.pts
      var curPts = self.pts;
      len = newPts.length;
      for (p = 0; p < len; p++) {
        if (newPts[p].x != -1) {
          curPts.push(newPts[p]);
        }
      }
      computeGeometry_(); // recompute center point, bounding box
    }
    return nPtsRemoved;
  };
  // ............................................ countDuplicates
  CREGION_POINTS.countDuplicates = function () {
    var dupCount = 0;
    for (var p = 1, len = self.pts.length; p < len; p++) {
      if (self.pts[p].isEqual(self.pts[p - 1])) { dupCount++; }
    }
    return dupCount;
  };
  // ............................................ removeDuplicatePtsAtEnd
  CREGION_POINTS.removeDuplicatePtsAtEnd = function () {
    var dupCount = 0;
    var ptList = self.pts;
    var lastIdx = ptList.length - 1;

    var pt1 = ptList[lastIdx];
    var pt0 = ptList[lastIdx - 1];
    while (lastIdx > 1 && pt0.x == pt1.x && pt0.y == pt1.y) {
      dupCount++;
      ptList.length = (ptList.length - 1); // remove pt1
      pt1 = pt0;
      lastIdx--;
      pt0 = ptList[lastIdx - 1];
    }
    computeGeometry_(); // recompute center point, bounding box
    return dupCount;
  };
  // ............................................ setLastPtToFirstPt
  CREGION_POINTS.setLastPtToFirstPt = function () {
    if (self.pts.length) {
      var pt0 = self.pts[0];
      var pt99 = self.pts[self.pts.length - 1];
      if (pt0.x != pt99.x && pt0.y != pt99.y) {
        var pt = new Point(pt0.x, pt0.y);
        self.addPt(pt);
      }
    }
  };
  // ............................................ region computeGeometry_
  // This function computes the center point and the bounding box of the region.
  //    Note that the center point is biased for regions with few points and first==last
  //    and that the center of the bbox could be used.

  var computeGeometry_ = function () {
    // really I know pins are one point, just use them
    if (self.type == us.eAnTypes.pin) {
      // Note that in the original code the line above referred to `this` rather than `self`, and `this` sometimes
      // pointed at `window`, so this branch never actually executed, so I'm not sure what it was supposed to do.
      // After correcting the context .setPoint started failing because self.pts[0] was empty. TODO: Figure out what it's supposed to be doing!
      if (self.pts.length) {
        self.centerPt.setPoint(self.pts[0]);
        // todo: implement bbox for pin, arrow, self.boundingBox.setxywh();
      }
    } else {
      if (self.pts.length) {
        var xsum = 0;
        var ysum = 0; // compute the centerPt once
        var xleft = Number.MAX_VALUE;
        var ytop = Number.MAX_VALUE; //
        var xright = Number.MIN_VALUE;
        var ybottom = Number.MIN_VALUE; //
        for (var p = 0, len = self.pts.length; p < len; p++) {
          var x = +self.pts[p].x;
          var y = +self.pts[p].y;
          xsum += x;
          ysum += y;
          if (x < xleft) xleft = x;
          if (y < ytop) ytop = y;
          if (x > xright) xright = x;
          if (y > ybottom) ybottom = y;
        }
        self.centerPt.set(xsum / self.pts.length, ysum / self.pts.length);
        self.centerPt.toInt();
        self.boundingBox.set(xleft, ytop, xright, ybottom);
        // Note that ImageServer has a limitation on the dimensions of its returned JPEG compressed image
        // (currently clipping each dimension to 2000 pixels). So this limitiation is used to define smallness.
        self.isSmall = (self.boundingBox.w < us.IMAGESERVER_MAXJDIM && self.boundingBox.h < us.IMAGESERVER_MAXJDIM);
      }
    }
  };

    // ............................................ getCenter
  CREGION_GET.getCenter = function (opt_bForce) {
    if (opt_bForce == false) { // if not forcing a recomputation
      // if centerPt has been computed or if it cannot yet be computed, return it
      if (self.centerPt.x > 0 || isUnDefined(self.pts) || self.pts.length === 0) { return self.centerPt; }
    }
    computeGeometry_();
    return self.centerPt;
  };
  // ............................................ getBBox
  CREGION_GET.getBBox = function (opt_bForce) {
    if (opt_bForce == false) { // if not forcing a recomputation
      // if boundingBox has been computed or if it cannot yet be computed, return it
      if (self.boundingBox.isZero() == false || isUnDefined(self.pts) || self.pts.length === 0) { return self.boundingBbox; }
    }
    computeGeometry_();
    return self.boundingBbox;
  };
  // ............................................ Region isInView
  CREGION.isInView = function (cr2View) {
    return self.boundingBox.hasOverlap(cr2View);
  };
  CREGION_GET.getIntersectRect = function (cr2View) {
    return self.boundingBox.getIntersectRect(cr2View);
  };

  // ............................................ getStrokeWidth
  // toconsider-could have var of strokeWidth in the region and only return differently if isHighLight - faster
  CREGION_GET.getStrokeWidth = function () {
    if (self.isHighLight && self.isCopyMoving) {
      return us.annotSpecs.strokeWidthCopyMove;
    } else if (self.shape === 'pin') {
      return us.annotSpecs.strokeWidthPin;
    } else if (self.shape === 'text') {
      return us.annotSpecs.strokeWidthTextBorder;
    } else {
      return us.annotSpecs.strokeWidth;
    }
  };

  // ............................................ buildXML
  //    buildXML - This function converts a Region to XML
  //        input parameters:
  //            selected {true, false}
  // note:
  //        o region type of negative pen is translated to a pen with a NegativeROA for backward compatibility
  //        o region type of polygon is translated to a pen for backward compatibility
  //        o region LineColor is saved as BGR for backward compatibility
  //        o Analyze is saved but should not be known to any viewer, still probably need it for backward compatibility
  //        o InputRegionId is set and saved by AAF; ImageScope defaults a parent region's value to "0"

  CREGION.buildXML = function (selected) {
    // if my local type is negative pen, save it as a pen (with NegativeROA set)
    var saveType = (self.type == us.eAnTypes.negpen) ? us.eAnTypes.pen : self.type;
    // if my local type is polygon, save it as a pen
    saveType = (self.type == us.eAnTypes.polygon) ? us.eAnTypes.pen : self.type;

    var regionXML = '<Region Id="' + self.regionId + '"' +
            ' Type="' + saveType + '"' +
            ' Zoom="' + 1.0 / self.zoom + '"' + // the annotation zoom is the inverse of the image zoom
            ' Selected="' + ((isUnDefined(selected) || selected == false) ? 0 : 1) + '"' +
            ' LineColor="' + self.decBGR + '"' +
            //        + ' ImageLocation=""'
            //        + ' ImageFocus=""'
            ' NegativeROA="' + ((self.type == us.eAnTypes.negpen) ? 1 : 0) + '"' +
            ' InputRegionId="' + (self.InputRegionId ? self.InputRegionId : '0') + '"' +
            ' Analyze="' + us.eAnAnalysis[self.type] + '"' +
            ' DisplayId="' + self.displayId + '"';
    if (self.data.lengthPixels) {
      regionXML += ' Length="' + self.data.lengthPixels + '"';
    }
    if (self.data.lengthMicrons) {
      regionXML += ' LengthMicrons="' + self.data.lengthMicrons + '"';
    }
    if (self.note) {
      regionXML += ' Text="' + escape(self.note) + '"';
    }
    regionXML += '>';

    if (isExported) {
      regionXML += '<Export>true</Export>';
    }

    var vertexXML = self.buildVertexXML();
    regionXML += '<Vertices>' + vertexXML + '</Vertices>' + '</Region>';
    return regionXML;
  };
  // ............................................ buildVertexXML
  //    buildVertexXML - This function converts an array of Points to XML
  CREGION.buildVertexXML = function () {
    var ptList = self.getPts();
    var str = '';
    for (var v = 0, vlen = ptList.length; v < vlen; v++) { str += '<Vertex X="' + ptList[v].x + '" Y="' + ptList[v].y + '"></Vertex>'; }
    return str;
  };

  // ............................................ distance_
  //    Region.distance - This function computes the distance in pixels or microns of an array
  //                            of Points or of a passed value.
  // input parms:
  //        "pointList" :     an array of Point pixel values
  //        "value" :        the pixel value to convert to microns
  //        "toMicrons":    boolean, convert to microns

  var distance_ = function (parms) {
    var distInPix = 0;
    if (parms.pointList) {
      var ptList = parms.pointList;
      for (var i = 1, len = ptList.length; i < len; i++) {
        var apt = ptList[i - 1];
        var bpt = ptList[i];
        distInPix += apt.distance(bpt);
      }
    } else if (parms.value) {
      distInPix = parms.value;
    }
    if (distInPix) {
      return (parms.toMicrons) ? roundToNearset(distInPix * viewport.getBaseMPP(), 10) : Math.round(distInPix);
    } else {
      return distInPix;
    }
  };

  // ............................................ construction
  (function construct () {
    self.updateProps(props, propSource, layer);

    // Unfortunately this class is used to both create new regions and restore them from the database. This makes it
    // hard to know if this is truly a new region or if it's just been restored from the database. We assume that if
    // we have a display or region id it's been restored, otherwise we generate one on the fly.
    if (layer) {
      if (!self.displayId) {
        // For consistency with ImageScope, the "displayId" is simply the nth region in the layer. To account for gaps
        // (e.g. if someone deletes the first region") we assume "displayId" is a running counter.
        if (layer.model.regions.length === 0) {
          self.displayId = 1;
        } else {
          self.displayId = Math.max(...layer.model.regions.map(r => Number(r.displayId))) + 1;
        }
      }
      if (!self.regionId) {
        // The initial regionId is negative and will be updated when the Save request completes. Although it is temporary
        // it should still be globally unique to simplify external reference updates
        self.setRegionId(-(++regionCounter));
      }

      layer.addRegion(self);
    } else if (!props.isHighLight) {
      logger.warn(`Layer not supplied for region ${self.regionId}; this may cause downstream issues...`);
    }

    if (propSource === 'x2js') {
      draw();
    }
  }());
}

let regionCounter = 0;



// WEBPACK FOOTER //
// ./src/uScope/js/extensions/annotations/regions/region.js